import type { TocHeading } from '../model/docs.type';

/** 제목이 하나뿐인 문서에 목차를 그리면 같은 말이 두 번 보인다. */
const MIN_HEADINGS = 2;
/** 번호가 두 자리가 되는 절 수 — 번호 칸을 넓힌다. */
const TWO_DIGITS = 10;

interface DocsTocProps {
  headings: readonly TocHeading[];
  activeId?: string;
  /** 항목을 누르면 바로 표시한다. 이동 자체는 앵커(`#s-3`)가 한다. */
  onSelect?: (id: string) => void;
}

/** 우측 목차. 한 단계뿐이고, 본문 제목과 같은 번호를 앞에 단다. */
export function DocsToc({ headings, activeId, onSelect }: DocsTocProps) {
  if (headings.length < MIN_HEADINGS) return null;

  // 가장 큰 번호(자릿수 + 마침표)가 들어가는 폭. `ch` 는 굵게(현재 항목)일 때 넓어져
  // 그 줄만 어긋나므로 굵기와 무관한 `em` 을 쓴다. 절이 100개를 넘는 문서는 없다.
  const numberWidth = headings.length >= TWO_DIGITS ? 'w-[1.6em]' : 'w-[1em]';

  return (
    <nav aria-label="이 페이지의 목차">
      <p className="mb-3 text-[11px] font-extrabold tracking-widest text-neutral-400">
        이 페이지
      </p>
      <ul className="border-l-2 border-neutral-100 dark:border-white/8">
        {headings.map((heading) => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              onClick={() => onSelect?.(heading.id)}
              className={[
                '-ml-0.5 flex gap-1.5 border-l-2 py-1.5 pl-3 text-[12.5px] leading-normal transition-colors',
                heading.id === activeId
                  ? 'border-brand-500 font-bold text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200',
              ].join(' ')}
            >
              {/* 번호 칸 폭을 고정한다 — `9.` 와 `10.` 의 폭이 달라 제목 시작점이 들쭉날쭉해진다. */}
              <span
                className={[
                  'shrink-0 text-right tabular-nums text-brand-500 dark:text-brand-400',
                  numberWidth,
                ].join(' ')}
              >
                {heading.number}.
              </span>
              <span>{heading.text}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
